/** @jsx jsx */
// External libraries
import { jsx } from '@emotion/core';
import { array, bool, func, shape, string } from 'prop-types';
import { FieldError, FloatingLabelSelect } from '@checkout/react-components';

// Internal modules
import { getContent } from '@/lib';
import { FLAG_SPRITE_POSITION_MAP } from '@/styles';
import { validatePhoneType } from './utils';
import {
  dialingCodeStyles,
  dropdownLabelStyleOverrides,
  getDropdownContainerStyleOverrides,
  phoneTypeStyleOverrides,
  phoneTypeStyleOverridesDialingCodeChange,
} from './styles';
import {
  ComponentContainer,
  InputsContainer,
  PhoneConfirmationText,
} from './StyledComponents';
import { StyledText } from '../../Common';

const PhoneContainer = props => {
  if (!props.withPhoneType) {
    return props.children;
  }

  return (
    <ComponentContainer>
      <InputsContainer
        css={[props.canDialingCodeChange && dialingCodeStyles]}
        {...props}
      >
        {props.children}
      </InputsContainer>

      {props.phone.displayError && (
        <FieldError id="phone-error">{props.phone.error}</FieldError>
      )}
    </ComponentContainer>
  );
};

PhoneContainer.propTypes = {
  canDialingCodeChange: bool.isRequired,
  phone: shape({
    displayError: bool.isRequired,
    error: string.isRequired,
  }).isRequired,
  withPhoneType: bool.isRequired,
};

/**
 * Spread props:
 * - properties from phoneType store object
 * - blur, change, and focus handlers
 * - disabled attribute
 */
const PhoneType = props => {
  const { canDialingCodeChange, ...propsToSpread } = props;
  return (
    <FloatingLabelSelect
      floatingLabelContainerStyleOverrides={
        canDialingCodeChange
          ? phoneTypeStyleOverridesDialingCodeChange
          : phoneTypeStyleOverrides
      }
      focusedClassName="is-focused"
      id="phoneType"
      label={getContent('fieldLabels.phoneType')}
      name="phoneType"
      required={true}
      selectOptions={[
        { text: getContent('phoneTypes.mobile'), value: 'MOBILE' },
        { text: getContent('phoneTypes.home'), value: 'HOME' },
        { text: getContent('phoneTypes.work'), value: 'WORK' },
      ]}
      validationFn={validatePhoneType}
      {...propsToSpread}
    />
  );
};

PhoneType.propTypes = {
  blurHandler: func,
  canDialingCodeChange: bool.isRequired,
  changeHandler: func.isRequired,
  disabled: bool,
  focusHandler: func,
  phoneType: shape({
    displayError: bool.isRequired,
    error: string.isRequired,
    value: string.isRequired,
  }).isRequired,
};

const DialingCodeSelect = props => {
  // Added 16 to the index to center it in the select
  // component, without which the flag would cut in half.
  const flagPos = FLAG_SPRITE_POSITION_MAP[props.value] + 16;
  const { dialingCodeSelectOptions, ...propsToSpread } = props;

  return (
    <FloatingLabelSelect
      floatingLabelContainerStyleOverrides={getDropdownContainerStyleOverrides(
        flagPos,
      )}
      focusedClassName="is-focused"
      id="dialingCode"
      name="dialingCode"
      required={true}
      selectOptions={dialingCodeSelectOptions}
      selectStyleOverrides={dropdownLabelStyleOverrides}
      {...propsToSpread}
    />
  );
};

DialingCodeSelect.propTypes = {
  blurHandler: func,
  changeHandler: func.isRequired,
  dialingCode: shape({
    displayError: bool.isRequired,
    error: string.isRequired,
    value: string.isRequired,
  }).isRequired,
  dialingCodeSelectOptions: array.isRequired,
  disabled: bool,
  focusHandler: func,
  value: string.isRequired,
};

const CupOtpMessage = () => (
  <StyledText css={{ marginBottom: '8px' }}>
    {getContent('oneTimePasswordConfirmation.message.initiateOtp', {
      mobileNumberOnFile: (
        <span css={{ fontWeight: '600' }}>
          {getContent('oneTimePasswordConfirmation.message.mobileNumberOnFile')}
        </span>
      ),
    })}
  </StyledText>
);

export {
  CupOtpMessage,
  DialingCodeSelect,
  PhoneContainer,
  PhoneConfirmationText,
  PhoneType,
};
